import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import StatusBadge from "../components/StatusBadge";
import { gbp, dateShort, dateTimeShort } from "../lib/format";

type RevertTarget = "draft" | "submitted" | "manager_approved" | "payroll_approved";

type Tab = "manager" | "payroll" | "awaiting-payment" | "history";

const ACTION_LABEL: Record<string, string> = {
  approve: "Approved",
  reject: "Rejected",
  mark_paid: "Marked paid",
  revert: "Reverted",
};

const ACTION_CLS: Record<string, string> = {
  approve: "bg-emerald-50 text-emerald-700",
  reject: "bg-red-50 text-red-700",
  mark_paid: "bg-emerald-100 text-emerald-800",
  revert: "bg-amber-100 text-amber-800",
};

export default function ApprovalsPage({ defaultStage = "manager" as Tab }: { defaultStage?: Tab }) {
  const [tab, setTab] = useState<Tab>(defaultStage);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<{ ok: number; failed: { id: string; reason: string }[] } | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["approvals", tab],
    queryFn: () => api.approvals.list(tab),
  });
  const viewingAsAdmin = (data as any)?.viewing_as_admin;

  // Reset selection when tab changes
  const items = data?.items ?? [];
  const allOnPageIds = useMemo(() => items.map((c: any) => c.id), [items]);
  const allSelected = allOnPageIds.length > 0 && allOnPageIds.every((id: string) => selected.has(id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      if (allOnPageIds.every((id: string) => prev.has(id))) {
        const next = new Set(prev);
        for (const id of allOnPageIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of allOnPageIds) next.add(id);
      return next;
    });
  }

  const bulkAction: "approve" | "mark-paid" | null =
    tab === "manager" || tab === "payroll" ? "approve" : tab === "awaiting-payment" ? "mark-paid" : null;
  const bulkLabel = bulkAction === "approve" ? "Approve selected" : "Mark selected as paid";
  const bulkVerb = bulkAction === "approve" ? "approve" : "mark paid";

  const [showRevert, setShowRevert] = useState(false);
  const [revertTo, setRevertTo] = useState<RevertTarget>("manager_approved");
  const [revertReason, setRevertReason] = useState("");

  // Available revert targets per tab — admins can roll back further than payroll-only users.
  const revertOptions: { value: RevertTarget; label: string }[] =
    tab === "awaiting-payment"
      ? [
          { value: "manager_approved", label: "Back to manager approved" },
          { value: "submitted", label: "Back to submitted (manager redo)" },
          { value: "draft", label: "Back to draft (claimant edits)" },
        ]
      : tab === "payroll"
      ? [
          { value: "submitted", label: "Back to submitted (manager redo)" },
          { value: "draft", label: "Back to draft (claimant edits)" },
        ]
      : tab === "manager"
      ? [{ value: "draft", label: "Back to draft (claimant edits)" }]
      : [];

  const bulk = useMutation({
    mutationFn: (override?: { action: "approve" | "mark-paid" | "revert"; extras?: any }) => {
      const a = override?.action ?? bulkAction!;
      return api.approvals.bulk(a as any, [...selected], override?.extras);
    },
    onSuccess: (res) => {
      setBulkResult({ ok: res.ok.length, failed: res.failed });
      setSelected(new Set());
      setShowRevert(false);
      setRevertReason("");
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
    onError: (e) => setBulkResult({ ok: 0, failed: [{ id: "—", reason: (e as Error).message }] }),
  });

  const showCheckboxes = bulkAction !== null;
  const showRevertButton = revertOptions.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Approvals</h1>
        <p className="text-slate-600 mt-1">Pending claims awaiting your action, plus what you've already actioned.</p>
        {viewingAsAdmin && tab !== "history" && (
          <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-1.5 rounded-full">
            <span className="font-semibold">Admin view</span>
            <span>· You're seeing claims across the whole org and can action on behalf of the assigned approver.</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {([
          { id: "manager", label: "Manager queue" },
          { id: "payroll", label: "Payroll queue" },
          { id: "awaiting-payment", label: "Awaiting payment" },
          { id: "history", label: "History" },
        ] as { id: Tab; label: string }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setSelected(new Set()); setBulkResult(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id
                ? "border-liaise-navy text-liaise-navy"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {showCheckboxes && selected.size > 0 && !showRevert && (
        <div className="bg-liaise-navy text-white rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> selected
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setSelected(new Set())} className="text-white/80 text-sm hover:text-white">
              Clear
            </button>
            {showRevertButton && (
              <button
                onClick={() => { setShowRevert(true); setRevertTo(revertOptions[0].value); }}
                className="bg-amber-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-amber-600"
              >
                Revert…
              </button>
            )}
            <button
              onClick={() => {
                if (confirm(`${bulkLabel}? This will ${bulkVerb} ${selected.size} claim${selected.size === 1 ? "" : "s"}.`)) {
                  bulk.mutate(undefined);
                }
              }}
              disabled={bulk.isPending}
              className="bg-white text-liaise-navy text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-50"
            >
              {bulk.isPending ? "Working…" : bulkLabel}
            </button>
          </div>
        </div>
      )}

      {showRevert && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="font-medium text-amber-900">Revert {selected.size} claim{selected.size === 1 ? "" : "s"}</h3>
            <button onClick={() => setShowRevert(false)} className="text-xs text-amber-700 hover:underline">Cancel</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block font-medium text-slate-700 mb-1">Send back to</span>
              <select
                value={revertTo}
                onChange={(e) => setRevertTo(e.target.value as RevertTarget)}
                className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white"
              >
                {revertOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium text-slate-700 mb-1">Reason (sent to each claimant)</span>
              <input
                type="text"
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                placeholder="e.g. Bank details incorrect, BACS bounced."
                className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => bulk.mutate({ action: "revert", extras: { to_status: revertTo, reason: revertReason.trim() } })}
              disabled={!revertReason.trim() || bulk.isPending}
              className="bg-amber-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {bulk.isPending ? "Reverting…" : `Confirm revert (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {bulkResult && (
        <div className={`rounded-xl px-5 py-3 text-sm border ${bulkResult.failed.length === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
          <div>
            <strong>{bulkResult.ok}</strong> succeeded
            {bulkResult.failed.length > 0 && <>, <strong>{bulkResult.failed.length}</strong> failed</>}.
          </div>
          {bulkResult.failed.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {bulkResult.failed.map((f, i) => (
                <li key={i}>· <code className="font-mono">{f.id.slice(0, 8)}</code> — {f.reason}</li>
              ))}
            </ul>
          )}
          <button onClick={() => setBulkResult(null)} className="text-xs underline mt-2">Dismiss</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {error && <div className="px-6 py-12 text-red-600 text-sm">{(error as Error).message}</div>}
        {data && data.items.length === 0 && (
          <div className="px-6 py-12 text-center text-slate-500">
            {tab === "history" ? "Nothing in your action history yet." : "Nothing waiting on you."}
          </div>
        )}
        {data && data.items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                {showCheckboxes && (
                  <th className="px-4 py-3 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  </th>
                )}
                <th className="px-6 py-3 font-medium">Number</th>
                <th className="px-6 py-3 font-medium">Claimant</th>
                <th className="px-6 py-3 font-medium">Cost centre</th>
                <th className="px-6 py-3 font-medium">Lines</th>
                <th className="px-6 py-3 font-medium text-right">Total</th>
                <th className="px-6 py-3 font-medium">{tab === "history" ? "My action" : tab === "awaiting-payment" ? "Approved" : "Submitted"}</th>
                <th className="px-6 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((c: any) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  {showCheckboxes && (
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                    </td>
                  )}
                  <td className="px-6 py-3">
                    <Link to={`/approvals/${c.id}?stage=${tab}`} className="text-liaise-navy font-medium hover:underline">
                      {c.request_number}
                    </Link>
                  </td>
                  <td className="px-6 py-3">
                    <div className="font-medium text-slate-700 flex items-center gap-1.5">
                      <Link to={`/admin/claimant/${c.claimant_entra_oid}`} className="hover:text-liaise-navy hover:underline">{c.claimant_display_name}</Link>
                      {c.manager_overridden ? (
                        <span
                          title={`Approver was changed from the default Entra manager. Set by: ${c.manager_override_set_by_email ?? "unknown"}${c.manager_override_reason ? `\n\nReason: ${c.manager_override_reason}` : ""}`}
                          className="bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 rounded cursor-help"
                        >
                          ⚠ overridden
                        </span>
                      ) : (
                        <span
                          title="Approver auto-resolved from Microsoft Entra (default manager)."
                          className="bg-emerald-50 text-emerald-700 text-[10px] font-medium px-1.5 py-0.5 rounded cursor-help"
                        >
                          ✓ default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{c.claimant_email}</div>
                    {viewingAsAdmin && c.manager_email && (
                      <div className="text-xs text-slate-600 mt-1">
                        Approver: <span className="font-medium">{c.manager_email}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3 text-slate-600">{c.cost_centre_name}</td>
                  <td className="px-6 py-3 text-slate-600">{c.line_count}</td>
                  <td className="px-6 py-3 text-right font-medium">{gbp(Number(c.total_amount))}</td>
                  <td className="px-6 py-3 text-slate-600">
                    {tab === "history" ? (
                      <div className="space-y-1">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${ACTION_CLS[c.my_last_action] ?? "bg-slate-100 text-slate-700"}`}>
                          {ACTION_LABEL[c.my_last_action] ?? c.my_last_action}
                          {c.my_last_stage && c.my_last_stage !== "claimant" ? ` · ${c.my_last_stage}` : ""}
                        </span>
                        <div className="text-xs text-slate-500">{dateTimeShort(c.my_last_acted_at)}</div>
                        {c.my_last_reason && <div className="text-xs italic text-slate-500 max-w-xs truncate">"{c.my_last_reason}"</div>}
                      </div>
                    ) : tab === "awaiting-payment" ? (
                      dateShort(c.payroll_actioned_at)
                    ) : (
                      dateShort(c.submitted_at ?? c.manager_actioned_at)
                    )}
                  </td>
                  <td className="px-6 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
